import { PureComponent, Fragment } from 'react'
import loadable from '@loadable/component'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import PropTypes from 'prop-types'
import styled, { ThemeProvider } from 'styled-components'
import { withStorageContext } from 'react-storage-context'
import { uniq, isEqual } from 'lodash'
import { formatStory, fetchStoryBySlug, trendingUsername } from 'shared/redux/fetch-stories'
import { toggleEditMode, updateEditMode } from 'shared/redux/edit-mode'
import Sidebar from 'shared/components/user/sidebar'
import { grid9 } from 'shared/css/grid.css'
import Info from './info'
import Gifs from './gifs'
import Footer from './footer'
import MoreStories from './more-stories'
import StoryCarousel from './more-stories/carousel'
import * as selectors from 'shared/redux/reducers'
import routes, { getRouteFromPathname } from 'shared/routes'
import { WhenInView } from 'shared/util/in-view'

const GifEditPanel = loadable(() =>
    import(/* webpackChunkName: "gifEditPanel" */ 'shared/components/edit-panel/gif-modal')
)

const Container = styled.div`
    align-items: flex-start;
    display: flex;
    padding-top: 36px;
    width: 100%;
`

const Details = styled.div`
    text-align: left;
    width: ${grid9};
`

const Private = styled.div`
    font-size: 24px;
    font-weight: bold;
    text-align: center;
    width: 100%;

    &:before {
        background: no-repeat url('/static/img/error_pages/text/sorry.gif') center top;
        background-size: contain;
        content: '';
        display: block;
        height: 312px;
        margin: 0 auto 20px;
        width: 512px;
    }
`

const isFromTrending = (state = {}) => {
    const { returnRoute = {} } = state
    return returnRoute.pathname && getRouteFromPathname(returnRoute.pathname) === routes.HOME
}

@withStorageContext
@withRouter
@connect(
    (state, { location, slug, local: { watchedStories = [] } }) => {
        const gifs = {}
        selectors.getGifs(state).map((gif) => {
            gifs[gif.id] = gif
        })
        return {
            gifs,
            isFromTrending: isFromTrending(location.state),
            editMode: selectors.getEditMode(state),
            trendingStories: selectors.getStoriesByUsername(state, trendingUsername),
            usernameStories: selectors.getMoreStoriesBySlug(state, slug, watchedStories),
        }
    },
    { fetchStoryBySlug, toggleEditMode, updateEditMode }
)
export default class Story extends PureComponent {
    static propTypes = {
        slug: PropTypes.string.isRequired,
    }
    state = {
        story: null,
        isPrivate: false,
    }
    static getDerivedStateFromProps(
        {
            // when we integrate this we can load more
            // local: { watchedStories },
            match: {
                params: { slug },
            },
            usernameStories,
            trendingStories,
            isFromTrending,
        },
        prevState
    ) {
        let moreStories = usernameStories
        if (trendingStories.length && isFromTrending) {
            moreStories = trendingStories
        }
        moreStories = moreStories.filter((story) => story.slug !== slug)
        return isEqual(moreStories, prevState.moreStories) ? null : { moreStories }
    }
    async componentDidMount() {
        const {
            slug,
            fetchStoryBySlug,
            saveLocal,
            local: { watchedStories = [] },
        } = this.props
        try {
            const story = await fetchStoryBySlug(slug, true)
            const formattedStory = formatStory(story.data)

            // mark story as seen
            saveLocal({ watchedStories: uniq([...watchedStories, formattedStory.story_id]) })

            this.setState({
                story: formattedStory,
            })
        } catch (error) {
            this.setState({
                isPrivate: true,
            })
        }
    }
    closeEditMode = () => {
        const { toggleEditMode, updateEditMode } = this.props
        toggleEditMode()
        updateEditMode()
    }
    render() {
        const { user, slug, gifs, editMode, isFromTrending } = this.props
        const { story, isPrivate, moreStories = [] } = this.state
        const { permissions = {}, isOwner = false } = user || {}
        const showDashboardLink = permissions.dashboard_analytics && isOwner
        return (
            <Container>
                {isPrivate && <Private>This story is private.</Private>}
                {!!story && (
                    <Fragment>
                        <Sidebar showDashboardLink={showDashboardLink} user={story.user}>
                            {moreStories.length > 0 && <MoreStories stories={moreStories} />}
                        </Sidebar>
                        <ThemeProvider theme={{ primaryColor: story.color, secondaryColor: story.secondary_color }}>
                            <Details>
                                <Info {...story} />
                                <Gifs cards={story.cards} gifs={gifs} />
                                <Footer slug={slug} />
                                <WhenInView dontUnmount height={227}>
                                    {story.user && story.user.username && moreStories.length > 0 && (
                                        <StoryCarousel
                                            username={isFromTrending ? trendingUsername : story.user.username}
                                            stories={moreStories}
                                            title="Keep Watching"
                                        />
                                    )}
                                </WhenInView>
                            </Details>
                        </ThemeProvider>
                    </Fragment>
                )}
                {editMode && editMode.isEditing && <GifEditPanel onClose={this.closeEditMode} />}
            </Container>
        )
    }
}
